import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import TurkiyeHaritasi from './TurkiyeHaritasi';

// API URL'i tanımlama
const baseURL = process.env.REACT_APP_API_URL;

const fetcher = url => {
    const token = localStorage.getItem('token');

    return fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    }).then(res => {
        if (!res.ok) {
            const error = new Error('Yetkilendirme hatası veya sunucuya ulaşılamadı.');
            error.info = res.statusText;
            error.status = res.status;
            throw error;
        }
        return res.json();
    });
};

// Finansal verileri, anasayfadaki renkli etiket formatında gösterecek component
const FinansalStatItem = ({ label, value, color }) => {
    const itemStyle = { padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' };
    const labelStyle = { fontWeight: '500', backgroundColor: color || '#6c757d', color: 'white', padding: '4px 12px', borderRadius: '12px', textAlign: 'center' };
    const valueStyle = { fontWeight: 'bold', fontSize: '16px', color: '#272935' };
    const formattedValue = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);

    return (
        <div style={itemStyle}>
            <span style={labelStyle}>{label}</span>
            <span style={valueStyle}>{formattedValue}</span>
        </div>
    );
};

// Ana Finansal Detaylar component'i
function FinansalDetaylar() {
    const [summaryData, setSummaryData] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aktifFiltre, setAktifFiltre] = useState('bugun');
    const [tarihAraligi, setTarihAraligi] = useState({
        baslangic: new Date().toISOString().split('T')[0],
        bitis: new Date().toISOString().split('T')[0]
    });
    const { data: ilListesi, error: ilError } = useSWR(
        `api/konum/iller`,
        fetcher
    );
    
    const [aktifUrunKategorisi, setAktifUrunKategorisi] = useState('Metrelik Ebat'); // <-- Yeni state

    // Türkiye saatine göre tarihi YYYY-MM-DD formatında döndürür
    const getTurkeyDateString = (date) => {
        const turkeyTime = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
        const year = turkeyTime.getFullYear();
        const month = String(turkeyTime.getMonth() + 1).padStart(2, '0');
        const day = String(turkeyTime.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // İlk useEffect: Sadece filtre değiştiğinde tarih aralığını güncelle
    useEffect(() => {
        const now = new Date();
        const turkeyNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
        const today = new Date(turkeyNow.getFullYear(), turkeyNow.getMonth(), turkeyNow.getDate());
        
        // 'aralik' modunda kullanıcı manuel tarih girdiği için tarih aralığını güncelleme
        if (aktifFiltre === 'aralik') {
            return;
        }

        let baslangic, bitis;
        
        switch (aktifFiltre) {
            case 'hafta': {
                const gunIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
                const haftaBasi = new Date(today);
                haftaBasi.setDate(today.getDate() - gunIndex);
                baslangic = getTurkeyDateString(haftaBasi);
                bitis = getTurkeyDateString(turkeyNow);
                break;
            }
            case 'ay':
                const ayBasi = new Date(turkeyNow.getFullYear(), turkeyNow.getMonth(), 1);
                baslangic = getTurkeyDateString(ayBasi);
                bitis = getTurkeyDateString(turkeyNow);
                break;
            case 'yil':
                const yilBasi = new Date(turkeyNow.getFullYear(), 0, 1);
                baslangic = getTurkeyDateString(yilBasi);
                bitis = getTurkeyDateString(turkeyNow);
                break;
            default: // 'bugun'
                const bugun = getTurkeyDateString(turkeyNow);
                baslangic = bugun;
                bitis = bugun;
                break;
        }

        setTarihAraligi({ baslangic, bitis });
    }, [aktifFiltre]);

    // İkinci useEffect: Tarih aralığı veya kategori değiştiğinde veri çek
    useEffect(() => {
        if (!tarihAraligi.baslangic || !tarihAraligi.bitis) {
            return;
        }

        setLoading(true);
        const kategoriParam = encodeURIComponent(aktifUrunKategorisi);
        
        Promise.all([
            fetch(`api/finansal/summary?start_date=${tarihAraligi.baslangic}&end_date=${tarihAraligi.bitis}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            }),
            fetch(`api/finansal/top-products-by-turnover?start_date=${tarihAraligi.baslangic}&end_date=${tarihAraligi.bitis}&category=${kategoriParam}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            })
        ])
        .then(async ([summaryRes, topProductsRes]) => {
            const summary = await summaryRes.json();
            const topProducts = await topProductsRes.json();
            if (summary.hata || topProducts.hata) throw new Error(summary.hata || topProducts.hata);
            setSummaryData(summary);
            setTopProducts(topProducts);
        })
        .catch(error => console.error("Finansal veriler çekilirken hata:", error))
        .finally(() => setLoading(false));
    }, [tarihAraligi, aktifUrunKategorisi]);

    // --- RENK KODLARI ---
    const kaynakRenkleri = { W: '#25D366', S: '#3783c2', R: '#f4bf4a' };
    const odemeRenkleri = { K: '#17bebb', E: '#6761a8', O: '#2c6184', D: '#bb999c' };
    const urunRenkleri = {
        hazirBaski: '#9B52E1',
        makineBaski: '#219852',
        topUrun: '#F3994A'
    };

    // --- ANASAYFA İLE AYNI STİLLER ---
    const styles = {
        main: { fontFamily: 'Poppins, sans-serif', padding: '20px' },
        header: { marginBottom: '20px' },
        mainBox: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', marginBottom: '30px' },
        sutun: { background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0', padding: '20px', display: 'flex', flexDirection: 'column' },
        sutunBaslik: { fontWeight: '600', paddingBottom: '10px', borderBottom: '2px solid #f0f0f0', marginBottom: '10px', fontSize: '16px' },
        toplamCard: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 },
        toplamValue: { fontSize: '65px', fontWeight: 'bold', color: '#272935', lineHeight: '1.2' },
        toplamLabel: { fontSize: '16px', color: '#6c757d', marginTop: '10px' },
        filterContainer: { display: 'flex', gap: '20px', marginBottom: '20px' },
        urunOzetGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '15px 40px', // Dikey ve Yatay boşluklar
            marginTop: '15px'
        },
        
        filterButton: (isActive) => ({
            padding: '8px 20px',
            fontFamily: "'Poppins', sans-serif",
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            width: '140px',
            textAlign: 'center',
            transition: 'all 0.2s ease',
            background: isActive ? '#3783c2' : '#ffffff', 
            color: isActive ? 'white' : '#333',
            border: isActive ? '1px solid #3783c2' : '1px solid #cccccc',
        }),

        urunOzetKutusu: {
            gridColumn: 'span 3', // 3 sütunluk yer kaplar
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #e0e0e0',
            padding: '20px',
        },    

        haritaKutusu: {
            gridColumn: 'span 3',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #e0e0e0',
            padding: '15px 20px',
            marginTop: '10px' // Dikey padding azaltıldı
        },

        dateRangeContainer: { background: '#dfe6e9', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' },
    };
    
    if (loading) return <div style={styles.main}><h2>Finansal Veriler Yükleniyor...</h2></div>;

    const formatAsCurrency = (value) => {
        const numberPart = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
        return `${numberPart} ₺`;
    };

    return (
        <div style={styles.main}>
            
            {/* Filtreleme Bölümü */}
            <div style={styles.filterContainer}>
                {['bugun', 'hafta', 'ay', 'yil', 'aralik'].map(f => {
                     let metin = f.charAt(0).toUpperCase() + f.slice(1);
                     if (f === 'bugun') metin = 'Bugün'; if (f === 'hafta') metin = 'Bu Hafta';
                     if (f === 'ay') metin = 'Bu Ay'; if (f === 'yil') metin = 'Bu Yıl';
                     if (f === 'aralik') metin = 'Tarih Aralığı';
                     return <button key={f} onClick={() => setAktifFiltre(f)} style={styles.filterButton(aktifFiltre === f)}>{metin}</button>
                })}
            </div>
            {aktifFiltre === 'aralik' && (
                <div style={styles.dateRangeContainer}>
                    <label>Başlangıç:</label>
                    <input type="date" value={tarihAraligi.baslangic} onChange={e => setTarihAraligi(p => ({...p, baslangic: e.target.value}))} style={{ padding: '5px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'poppins' }} />
                    <label>Bitiş:</label>
                    <input type="date" value={tarihAraligi.bitis} onChange={e => setTarihAraligi(p => ({...p, bitis: e.target.value}))} style={{ padding: '5px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'poppins' }} />
                </div>
            )}
            
            {summaryData && (
            <>
                {/* İLK ANA KUTU */}
                <div style={styles.mainBox}>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>Toplam Sipariş</div>
                            <div style={styles.toplamCard}>
                            <div style={styles.toplamValue}>
                                {formatAsCurrency(summaryData.toplam_odeme)}
                            </div>
                        </div>
                    </div>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>Sipariş Kaynağı Ciroları</div>
                        <FinansalStatItem label="Whatsapp" value={summaryData.kaynak_cirolari?.W} color={kaynakRenkleri.W} />
                        <FinansalStatItem label="Web Sitesi" value={summaryData.kaynak_cirolari?.S} color={kaynakRenkleri.S}/>
                        <FinansalStatItem label="Resmi Evrak" value={summaryData.kaynak_cirolari?.R} color={kaynakRenkleri.R}/>
                    </div>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>Ödeme Türü Ciroları</div>
                        <FinansalStatItem label="Kapıda Ödeme" value={summaryData.odeme_cirolari?.K} color={odemeRenkleri.K}/>
                        <FinansalStatItem label="EFT / Havale" value={summaryData.odeme_cirolari?.E} color={odemeRenkleri.E}/>
                        <FinansalStatItem label="Online Ödeme" value={summaryData.odeme_cirolari?.O} color={odemeRenkleri.O}/>
                        <FinansalStatItem label="Kurum Ödemesi" value={summaryData.odeme_cirolari?.D} color={odemeRenkleri.D}/>
                    </div>
                </div>

                {/* İKİNCİ ANA KUTU */}
                <div style={styles.mainBox}>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>V3100 Toplam Ciro</div>
                            <div style={styles.toplamCard}>
                            <div style={styles.toplamValue}>
                                {/* Artık para formatında gösteriliyor */}
                                {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summaryData.urun_cirolari?.['V3100 Toplam Ciro'] || 0)} ₺
                            </div>
                        </div>
                    </div>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>Makine Baskı Ciroları</div>
                        <FinansalStatItem label="Orta Ebat (35x50)" value={summaryData.urun_cirolari?.['Orta Ebat (35x50)']} color={urunRenkleri.makineBaski} />
                        <FinansalStatItem label="Küçük Ebat (21x30)" value={summaryData.urun_cirolari?.['Küçük Ebat (21x30)']} color={urunRenkleri.makineBaski} />
                        <FinansalStatItem label="Mini Ebat (15x21)" value={summaryData.urun_cirolari?.['Mini Ebat (15x21)']} color={urunRenkleri.makineBaski} />
                        <FinansalStatItem label="Uzun Ebat (35x70)" value={summaryData.urun_cirolari?.['Uzun Ebat (35x70)']} color={urunRenkleri.makineBaski} />
                    </div>
                    <div style={styles.sutun}>
                        <div style={styles.sutunBaslik}>Hazır Baskı Ciroları</div>
                        <FinansalStatItem label="Metrelik Ebat" value={summaryData.urun_cirolari?.['Metrelik Ebat']} color={urunRenkleri.hazirBaski} />
                        <FinansalStatItem label="Büyük Ebat" value={summaryData.urun_cirolari?.['Büyük Ebat']} color={urunRenkleri.hazirBaski} />
                        <FinansalStatItem label="Esmaül Hüsna" value={summaryData.urun_cirolari?.['Esmaül Hüsna']} color={urunRenkleri.hazirBaski} />
                        <FinansalStatItem label="Özel Baskı" value={summaryData.urun_cirolari?.['Özel Baskı']} color={urunRenkleri.hazirBaski} />
                    </div>
                </div>

                 {/* --- YENİ ÜÇÜNCÜ ANA KUTU --- */}
                <div style={styles.urunOzetKutusu}>
                    <div style={styles.sutunBaslik}>En Çok Satılan Ürünler</div>
                    <div style={{ display: 'flex', gap: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
                        {['Metrelik Ebat', 'Büyük Ebat', 'Esmaül Hüsna', 'Yan Ürünler'].map(kategori => (
                            <button key={kategori} onClick={() => setAktifUrunKategorisi(kategori)} style={styles.filterButton(aktifUrunKategorisi === kategori)}>
                                {kategori}
                            </button>
                        ))}
                    </div>

                    {/* Ürün Ciro Listesi Grid'i */}
                    <div style={styles.urunOzetGrid}>
                        {topProducts.length > 0 ? (
                            topProducts.map((urun, index) => (
                                <FinansalStatItem key={index} label={urun.urun_adi} value={urun.toplam_ciro} color={urunRenkleri.topUrun} />
                            ))
                        ) : (
                            <p style={{ gridColumn: 'span 3', textAlign: 'center', color: '#888' }}>Bu kategoride ciro bulunamadı.</p>
                        )}
                    </div>
                </div>
                <div style={{...styles.haritaKutusu, gridColumn: 'span 3', marginTop: '30px'}}>
                    <h3 style={styles.sutunBaslik}>Şehir Bazında Satış Dağılımı</h3>
                    {/* Haritaya artık il listesini de prop olarak gönderiyoruz */}
                    <TurkiyeHaritasi 
                        tarihAraligi={tarihAraligi} 
                        veritabaniIlleri={ilListesi} 
                    />
                </div>
            </>
            )}
        </div>
    );
}

export default FinansalDetaylar;